class ProductLightingOptions extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()
    const inputs = this.els.input.elements
    if (!inputs) return
    for (const input of inputs) {
      input.addEventListener('change', this.handleChange.bind(this))
    }

    this.listeners.add(
      this.closest('[data-product-detail-blocks]'),
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.handleQuantityChange.bind(this),
    )

    const popover = this.els['select-popover']?.element
    if (popover) {
      this.listeners.add(popover, 'toggle', this.handlePopoverToggle.bind(this))
    }

    const submitButton = this.getSubmitButton()
    if (submitButton) {
      submitButton.dataset.errorLabel = this.dataset.errorLabel
      this.listeners.add(
        submitButton,
        'click',
        this.handleFormSubmission.bind(this),
      )
    }

    const header = this.externalEls?.header?.element

    if (popover && header) {
      this.listeners.add(header, 'transitionend', () => {
        this.checkHeaderOverlap(header, popover)
      })
    }

    this.seedNestedItem()
  }

  checkHeaderOverlap (header, popover) {
    if (!popover.matches(':popover-open')) return

    const headerRect = header.getBoundingClientRect()
    const popoverRect = popover.getBoundingClientRect()

    const isIntersecting = !(
      popoverRect.bottom <= headerRect.top ||
      popoverRect.top >= headerRect.bottom
    )

    if (isIntersecting) {
      popover.hidePopover()
    }
  }

  handleFormSubmission (e) {
    const hasCheckedInput = this.els.input.elements?.some(
      input => input.checked,
    )

    if (hasCheckedInput) return

    e.preventDefault()
    e.stopImmediatePropagation()
    this.getSubmitButton()?.setAttribute('aria-invalid', 'true')
  }

  handleQuantityChange (e) {
    if (!e?.detail?.value) return
    this._currentQuantity = e.detail.value
    const quantityInput = this.getForm()?.querySelector(
      '[name="items[1][quantity]"]',
    )
    if (quantityInput) quantityInput.value = this._currentQuantity
  }

  getForm () {
    return (
      this.closest('[data-product-detail-blocks]')?.querySelector(
        '[data-product-form]',
      ) ?? document.querySelector('[data-product-form]')
    )
  }

  getSubmitButton () {
    return this.getForm()?.querySelector('[type="submit"]')
  }

  seedNestedItem () {
    const checked = this.els.input.elements?.find(input => input.checked)
    if (checked) {
      this.updateNestedItem(checked)
    }
  }

  isNoRewireOption (input) {
    return input.value === 'unwired' || input.dataset.isDefault === 'true'
  }

  updateLineItemProperties (input) {
    const form = this.getForm()
    if (!form) return

    const isNoRewire = this.isNoRewireOption(input)

    const properties = isNoRewire
      ? {}
      : {
          _rewire_service: input.dataset.productTitle || input.value,
          _nested_child_variant_ids: input.value,
        }

    for (const name of ['_rewire_service', '_nested_child_variant_ids']) {
      const existing = form.querySelector(`[name="properties[${name}]"]`)
      if (!(name in properties)) {
        existing?.remove()
        continue
      }
      let propertyInput = existing
      if (!propertyInput) {
        propertyInput = document.createElement('input')
        propertyInput.type = 'hidden'
        propertyInput.name = `properties[${name}]`
        form.appendChild(propertyInput)
      }
      propertyInput.value = properties[name]
    }
  }

  updateNestedItem (input) {
    const form = this.getForm()
    if (!form) return

    const isNoRewire = this.isNoRewireOption(input)

    let idInput = form.querySelector('[name="items[1][id]"]')
    let parentIdInput = form.querySelector('[name="items[1][parent_id]"]')
    let quantityInput = form.querySelector('[name="items[1][quantity]"]')

    if (isNoRewire) {
      idInput?.remove()
      parentIdInput?.remove()
      quantityInput?.remove()
      return
    }

    if (!idInput) {
      idInput = document.createElement('input')
      idInput.type = 'hidden'
      idInput.name = 'items[1][id]'
      form.appendChild(idInput)
    }

    if (!parentIdInput) {
      parentIdInput = document.createElement('input')
      parentIdInput.type = 'hidden'
      parentIdInput.name = 'items[1][parent_id]'
      form.appendChild(parentIdInput)
    }

    if (!quantityInput) {
      quantityInput = document.createElement('input')
      quantityInput.type = 'hidden'
      quantityInput.name = 'items[1][quantity]'
      form.appendChild(quantityInput)
    }

    idInput.value = input.value
    parentIdInput.value = form.querySelector('[name="id"]').value
    quantityInput.value =
      this._currentQuantity ||
      form.querySelector('[name="quantity"]')?.value ||
      1
  }

  handleChange (e) {
    const surcharge = this.parseSurcharge(e.target)

    this.getSubmitButton()?.setAttribute('aria-invalid', 'false')
    this.updateLineItemProperties(e.target)
    this.updateNestedItem(e.target)
    this.updateSelectTrigger(e.target)
    this.updateSelectDescription(e.target)

    this.els['select-popover']?.element?.hidePopover()

    window.BAO.dispatchEvent(window.BAO.EVENTS.LIGHTING.CHANGED, { surcharge })
  }

  handlePopoverToggle (e) {
    if (e.newState !== 'open') return

    const popover = this.els['select-popover']?.element
    const checked = this.els.input.elements?.find(
      input => input.checked && popover.contains(input),
    )
    if (checked) this.updateSelectTrigger(checked)
  }

  updateSelectTrigger (input) {
    const optionContent = input
      .closest('.prd-LightingSelectOption')
      ?.querySelector('.prd-LightingSelectOption_Content')
    const triggerText = this.els['select-trigger-text']?.element
    if (!optionContent || !triggerText) return

    const clone = optionContent.cloneNode(true)
    clone.querySelector('.prd-LightingSelectOption_Status')?.remove()

    triggerText.replaceChildren(clone)
  }

  updateSelectDescription (input) {
    const description = this.els['select-description']?.element
    if (!description) return

    const optionDescription = input
      .closest('.prd-LightingSelectOption')
      ?.querySelector('.prd-LightingSelectOption_DescriptionContent')

    if (!optionDescription || this.isNoRewireOption(input)) {
      description.setAttribute('aria-hidden', 'true')
      description.replaceChildren()
      return
    }

    const clone = optionDescription.cloneNode(true)
    clone.hidden = false

    description.replaceChildren(clone)
    description.setAttribute('aria-hidden', 'false')
  }

  parseSurcharge (input) {
    return parseInt(input.dataset.productLightingOptionsSurcharge, 10) || 0
  }
}

if (!window.customElements.get('product-lighting-options')) {
  window.customElements.define(
    'product-lighting-options',
    ProductLightingOptions,
  )
}
